import { z } from 'zod';

import {
  CursorPaginationMetaSchema,
  ListSortOrderSchema,
  PaginationSchema,
} from '../../pagination.js';

export const StockTransferStatusSchema = z.enum(['DRAFT', 'IN_TRANSIT', 'RECEIVED', 'CANCELLED']);

export type StockTransferStatus = z.infer<typeof StockTransferStatusSchema>;

export const StockTransferStatusFilterSchema = z.enum([
  'all',
  'DRAFT',
  'IN_TRANSIT',
  'RECEIVED',
  'CANCELLED',
]);

export type StockTransferStatusFilter = z.infer<typeof StockTransferStatusFilterSchema>;

export const StockTransferDerivedTypeSchema = z.enum([
  'intra_place',
  'cross_place',
  'external_doc',
  'auto_network',
]);

export type StockTransferDerivedType = z.infer<typeof StockTransferDerivedTypeSchema>;

export const ListStockTransfersParamsSchema = z.object({
  placeId: z.uuid(),
});

export const StockTransferListSortFieldSchema = z.enum([
  'displayNumber',
  'route',
  'itemCount',
  'totalQuantity',
  'type',
  'status',
  'updatedAt',
]);
export type StockTransferListSortField = z.infer<typeof StockTransferListSortFieldSchema>;

export const ListStockTransfersQuerySchema = PaginationSchema.extend({
  q: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(1).max(100).optional(),
  ),
  status: StockTransferStatusFilterSchema.optional().default('all'),
  sort: StockTransferListSortFieldSchema.optional(),
  order: ListSortOrderSchema.optional(),
});

export type ListStockTransfersQuery = z.infer<typeof ListStockTransfersQuerySchema>;

export const StockTransferListItemSchema = z.object({
  id: z.uuid(),
  displayNumber: z.string(),
  originPlaceId: z.uuid(),
  destinationPlaceId: z.uuid(),
  originPlaceName: z.string(),
  destinationPlaceName: z.string(),
  routeLabel: z.string(),
  status: StockTransferStatusSchema,
  derivedType: StockTransferDerivedTypeSchema,
  requiresExternalDoc: z.boolean(),
  isAutoNetwork: z.boolean(),
  itemCount: z.number().int().nonnegative(),
  totalQuantitySent: z.string(),
  productSummary: z.string().nullable(),
  updatedAt: z.iso.datetime(),
});

export type StockTransferListItem = z.infer<typeof StockTransferListItemSchema>;

export const ListStockTransfersResponseSchema = z.object({
  items: z.array(StockTransferListItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListStockTransfersResponse = z.infer<typeof ListStockTransfersResponseSchema>;

export const StockTransferSummaryParamsSchema = z.object({
  placeId: z.uuid(),
});

export const StockTransferSummaryResponseSchema = z.object({
  inTransitCount: z.number().int().nonnegative(),
  draftCount: z.number().int().nonnegative(),
  receivedInPeriodCount: z.number().int().nonnegative(),
  autoNetworkCount: z.number().int().nonnegative(),
  receivedPeriodDays: z.number().int().positive(),
});

export type StockTransferSummaryResponse = z.infer<typeof StockTransferSummaryResponseSchema>;
